// hooks/useVCardProfileSync.ts
import { useEffect, useCallback, useState, useRef } from "react";
import { VCardSynchronization } from "../services/vcard/synchronization";
import { useVCardStore } from "../store/vcardStore";
import { useUserStore } from "../store/userStore";
import { useDebugStore } from "../store/debugStore";
import type { VCardCategory } from "../types/vcard";
import debounce from "lodash/debounce";

// Utility function to compare values deeply
const areValuesEqual = (value1: any, value2: any): boolean => {
  if (value1 === value2) return true;

  // Handle null/undefined case
  if (value1 == null && value2 == null) return true;
  if (value1 == null || value2 == null) return false;

  // Use JSON stringify for objects and arrays, with fallback for circular references
  try {
    return JSON.stringify(value1) === JSON.stringify(value2);
  } catch (e) {
    // Fallback for non-serializable objects
    return false;
  }
};

export interface SyncConfig {
  syncFields: string[];
  debounceMs?: number;
}

export const useVCardProfileSync = (
  category: VCardCategory,
  config: Partial<SyncConfig> = {},
) => {
  const { vcards } = useVCardStore();
  const { profile } = useUserStore();
  const addDebugMessage = useDebugStore((state) => state.addMessage);

  // Find the vCard for this category
  const vcard = profile
    ? vcards.find((v) => v.userId === profile.id && v.category === category)
    : null;

  // Local state for field values and sync tracking
  const [localValues, setLocalValues] = useState<Record<string, any>>({});
  const [isSyncing, setIsSyncing] = useState(false);
  const lastSyncRef = useRef<Record<string, any>>({});
  const listenersRef = useRef<(() => void)[]>([]);
  const isSyncingRef = useRef(false);
  // Add a ref to track previous values for comparison
  const prevValuesRef = useRef<Record<string, any>>({});
  const localValuesRef = useRef(localValues);

  // Initialize localValues when vcard changes
  useEffect(() => {
    if (vcard) {
      // Extract values for all sync fields from the vcard
      const initialValues: Record<string, any> = {};

      if (config.syncFields?.length) {
        config.syncFields.forEach((field) => {
          // Handle nested paths
          const path = field.split(".");
          let value = vcard;

          // Navigate through the path to get the value
          for (const segment of path) {
            if (value && typeof value === "object" && segment in value) {
              value = value[segment];
            } else {
              value = undefined;
              break;
            }
          }

          initialValues[field] = value;
        });
      }

      // Check if the new values are different from the previous values
      // to prevent unnecessary state updates that could cause re-renders
      const isDifferent =
        JSON.stringify(initialValues) !== JSON.stringify(prevValuesRef.current);

      if (isDifferent) {
        prevValuesRef.current = initialValues;
        setLocalValues(initialValues);
        lastSyncRef.current = { ...initialValues };
        addDebugMessage("info", `Initialized localValues for ${category}`, {
          fields: Object.keys(initialValues),
          category,
          initialValues,
        });
      }
    }
  }, [vcard, config.syncFields, category, addDebugMessage]);

  // Create a debounced sync function
  const debouncedSyncField = useCallback(
    debounce(async (params) => {
      try {
        await VCardSynchronization.syncField(params);
      } catch (error) {
        addDebugMessage(
          "error",
          `Debounced sync failed for ${params.fieldId}`,
          {
            error,
            params,
          },
        );
        throw error;
      }
    }, config.debounceMs || 300),
    [config.debounceMs, addDebugMessage],
  );

  // Update a specific field with support for nested paths
  const updateField = useCallback(
    async (
      fieldId: string,
      value: any,
      source: "profile" | "vcard" = "profile",
    ) => {
      if (!vcard) {
        addDebugMessage("error", "Cannot update field: no vCard found", {
          fieldId,
          source,
        });
        return;
      }

      try {
        // Convert dot notation to path array
        const path = fieldId.split(".");
        const actualFieldId = path[path.length - 1];

        // Check if value actually changed from last sync or current local value
        const lastSyncValue = lastSyncRef.current[fieldId];
        const currentLocalValue = localValuesRef.current[fieldId];

        // Skip update if the value hasn't changed from either source
        if (
          areValuesEqual(lastSyncValue, value) ||
          areValuesEqual(currentLocalValue, value)
        ) {
          addDebugMessage(
            "info",
            `Skipping update for ${fieldId} - value unchanged`,
            {
              fieldId,
              valueType: typeof value,
            },
          );
          return;
        }

        // Prevent concurrent updates for the same field
        if (isSyncingRef.current) {
          addDebugMessage(
            "info",
            `Deferring update for ${fieldId} - sync in progress`,
            {
              fieldId,
            },
          );
          // We could implement a queue here if needed
          return;
        }

        setIsSyncing(true);
        isSyncingRef.current = true;

        // Use debounced sync to prevent rapid-fire updates
        await debouncedSyncField({
          fieldId: actualFieldId,
          sourceType: source,
          category,
          value,
          vcardId: vcard.id,
          path: path.length > 1 ? path : undefined,
        });

        // Update local state and sync reference
        setLocalValues((prev) => {
          // Only update if value has actually changed
          if (areValuesEqual(prev[fieldId], value)) {
            return prev; // No change needed
          }
          return {
            ...prev,
            [fieldId]: value,
          };
        });

        lastSyncRef.current[fieldId] = value;
      } catch (error) {
        addDebugMessage("error", `Failed to sync field ${fieldId}`, {
          error,
          fieldId,
          source,
        });
        throw error;
      } finally {
        setIsSyncing(false);
        isSyncingRef.current = false;
      }
    },
    [vcard, category, addDebugMessage, debouncedSyncField],
  );

  // Subscribe to field updates
  useEffect(() => {
    if (!config.syncFields?.length || !category) return;

    // Clean up any existing listeners
    listenersRef.current.forEach((unsubscribe) => unsubscribe());
    listenersRef.current = [];

    // Create new listeners for each field
    const newListeners = config.syncFields.map((field) => {
      return VCardSynchronization.registerFieldListener(
        field,
        category,
        (value) => {
          // Skip updates if we're currently syncing (prevents loops)
          if (isSyncingRef.current) {
            addDebugMessage(
              "info",
              `Skipping listener update for ${field} - sync in progress`,
              {
                field,
              },
            );
            return;
          }

          // Don't update if value hasn't changed from both local and last sync
          const lastSyncValue = lastSyncRef.current[field];
          const localValue = localValuesRef.current[field];

          if (
            areValuesEqual(value, lastSyncValue) ||
            areValuesEqual(value, localValue)
          ) {
            addDebugMessage(
              "info",
              `Skipping listener update for ${field} - value unchanged`,
              {
                field,
                valueType: typeof value,
              },
            );
            return;
          }

          // Prevent setting state during render phase - use setTimeout
          setTimeout(() => {
            // Check again in case something changed during the timeout
            if (isSyncingRef.current) return;

            setLocalValues((prev) => {
              // Final safety check - if value is the same as current state, don't update
              if (areValuesEqual(prev[field], value)) {
                return prev;
              }

              const newValues = {
                ...prev,
                [field]: value,
              };

              // Update the lastSyncRef to match the new state
              lastSyncRef.current[field] = value;

              // Log successful update
              addDebugMessage("info", `Field ${field} updated from listener`, {
                category,
                field,
                valueType: typeof value,
              });

              return newValues;
            });
          }, 0);
        },
      );
    });

    // Store new listeners for cleanup
    listenersRef.current = newListeners;

    // Cleanup function
    return () => {
      listenersRef.current.forEach((unsubscribe) => unsubscribe());
      listenersRef.current = [];
    };
  }, [category, config.syncFields, addDebugMessage]);

  useEffect(() => {
    localValuesRef.current = localValues;
  }, [localValues]);

  return {
    vcard,
    updateField,
    localValues,
    isSyncing,
  };
};

export default useVCardProfileSync;
